<script lang="ts">
    
    import 'swiper/css/bundle';
    import { fly } from "svelte/transition";
    import { Swiper, Manipulation } from 'swiper';
    import { createEventDispatcher, getContext, onMount } from "svelte";
    import type { MilestoneDto, OpenWinScreen } from "../../global";
    import IconClose from "../Shared/IconClose.svelte";
    import MilestoneLastPicture from '../Pictures/MilestoneLastPicture.svelte';
    import UploadMilestonePicture from '../Pictures/UploadMilestonePicture.svelte';
    import IconMag from '../Shared/IconMag.svelte';
    import MilestoneLightbox from '../Pictures/MilestoneLightbox.svelte';
    import IconChevron from '../Shared/IconChevron.svelte';

    export let milestones: MilestoneDto[];
    export let milestoneIndex: number;
    
    const dispatch = createEventDispatcher();
    const openWinScreen: OpenWinScreen = getContext('openWinScreen');
 
    let sliderContainer: HTMLElement;
    let swiper: Swiper;
    let currentMilestone: MilestoneDto;
    let dialogWidth = 300;
    let windowWidth = 500;
    let showNavBtns = true;

    $: updateNavBtnsVisibility(dialogWidth, windowWidth);

    onMount(() => {

        swiper = new Swiper(sliderContainer, {
            modules: [Manipulation],
            initialSlide: milestoneIndex,
            on: {
                slideNextTransitionStart: onSlideNextTransitionStart,
                slidePrevTransitionStart: onSlidePrevTransitionStart,
                slideNextTransitionEnd: onSlideNextTransitionEnd,
                slidePrevTransitionEnd: onSlidePrevTransitionEnd,
            }
        });

        //Añadimos un slide para el milestone actual
        swiper.appendSlide(createSlideElement(milestones[milestoneIndex]));

        // Añadidmos un slide para el milestone anterior
        appendPrevSlide();

        //Añadidmos un slide para el milestone siguiente
        appendNextSlide();

    });

    function updateNavBtnsVisibility(dialogWidth: number, windowWidth: number): void {
        showNavBtns = dialogWidth + (35 * 2) < windowWidth;
    }

    function onSlideNextTransitionStart(): void {
        if (!swiper) {
            return;
        }
        milestoneIndex++;
        if (milestoneIndex >= milestones.length) {
            milestoneIndex = 0;
        }
        dispatch('change', milestones[milestoneIndex]);
    }

    function onSlideNextTransitionEnd(): void {
        if (!swiper) {
            return;
        }
        appendNextSlide();
        removeFirstSlide();
    }

    function onSlidePrevTransitionStart(): void {
        if (!swiper) {
            return;
        }
        milestoneIndex--;
        if (milestoneIndex < 0) {
            milestoneIndex = milestones.length - 1;
        }
        dispatch('change', milestones[milestoneIndex]);
    }

    function onSlidePrevTransitionEnd(): void {
        if (!swiper) {
            return;
        }
        appendPrevSlide();
        removeLastSlide();
    }

    function appendPrevSlide(): void {
        let prevIndex = milestoneIndex - 1;
        if (prevIndex < 0) {
            prevIndex = milestones.length - 1;
        }
        swiper.prependSlide(createSlideElement(milestones[prevIndex]));
    }

    function appendNextSlide(): void {
        let nextIndex = milestoneIndex + 1;
        if (nextIndex >= milestones.length) {
            nextIndex = 0;
        }
        swiper.appendSlide(createSlideElement(milestones[nextIndex]));
    }

    function removeFirstSlide(): void {
        if (!swiper) {
            return;
        }
        swiper.removeSlide(0);
    }

    function removeLastSlide(): void {
        if (!swiper) {
            return;
        }
        swiper.removeSlide(swiper.slides.length - 1);
    }

    function onClose(): void {
        dispatch('close');
    }

    function createSlideElement(milestone: MilestoneDto): HTMLElement {
        const slideElement = document.createElement('div') as HTMLElement;
        const image = document.createElement('div') as HTMLElement;
        const magIcon = document.createElement('div') as HTMLElement;
        const name = document.createElement('div') as HTMLElement;
        const address = document.createElement('div') as HTMLElement;
        const uploadBtnContainer = document.createElement('div') as HTMLElement;

        slideElement.classList.add('swiper-slide');
        image.classList.add('milestone-image');
        magIcon.classList.add('mag-icon');
        name.classList.add('milestone-name');
        address.classList.add('address');
        uploadBtnContainer.classList.add('upload-btn');

        name.textContent = milestone.name;
        address.textContent = milestone.address;

        image.addEventListener('click', () => onImageClick(milestone));

        new MilestoneLastPicture({
            target: image,
            props: { milestoneId: milestone.id }
        });

        new IconMag({ target: magIcon });

        new UploadMilestonePicture({
            target: uploadBtnContainer,
            props: {
                milestone,
                openWinScreen,
            }
        });

        image.append(magIcon);
        slideElement.append(image, name, address, uploadBtnContainer);
        return slideElement;
    }

    function onImageClick(milestone: MilestoneDto): void {
        currentMilestone = milestone;
    }

    function onMilestoneLightboxClose(): void {
        currentMilestone = undefined;
    }

    function next(): void {
        swiper?.slideNext();
    }

    function prev(): void {
        swiper?.slidePrev();
    }

</script>

<svelte:window bind:innerWidth={windowWidth} />

<div class="milestone-modal" transition:fly={{ y: -30 }}>
    <div class="dialog" bind:offsetWidth={dialogWidth}>
        <button type="button" class="close-btn" on:click={onClose}>
            <IconClose size="20px" />
        </button>
        <div bind:this={sliderContainer} class="swiper">
            <div class="swiper-wrapper"></div>
        </div>
        {#if showNavBtns}
            <button type="button" class="swiper-nav swiper-next" on:click={next}>
                <IconChevron color="#fff" right />
            </button>
            <button type="button" class="swiper-nav swiper-prev" on:click={prev}>
                <IconChevron color="#fff" />
            </button>
        {/if}
    </div>
    <div class="arrow"></div>
</div>

{#if currentMilestone}
    <MilestoneLightbox milestone={currentMilestone} on:close={onMilestoneLightboxClose} />
{/if}

<style lang="scss">

    .milestone-modal {
        flex: 1 1 0;
        padding-top: 2rem;
        max-height: 400px;
        display: flex;
        flex-direction: column;
        align-items: center;
        pointer-events: none;
        touch-action: none;
        filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.25));

        :global(.swiper-slide) {
            position: relative;
            height: 100%;
            display: flex;
            flex-direction: column;
        }

        :global(.upload-btn) {
            display: grid;
            margin-top: 10px;
        }

        :global(.milestone-image) {
            flex: 1 1 0;
            background-color: #eee;
            margin-bottom: 8px;
        }

        :global(.mag-icon) {
            position: absolute;
            z-index: 2;
            top: 5px;
            left: 5px;
        }

        :global(.milestone-name) {
            font-size: 1.2rem;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        :global(.address) {
            text-align: center;
            font-style: italic;
            color: #999;
        }
    }

    .dialog {
        flex: 1 1 0;
        background-color: #fff;
        box-sizing: border-box;
        width: calc(100% - 30px);
        max-width: 300px;
        border-radius: 10px;
        padding: 1rem;
        pointer-events: auto;
        touch-action: auto;
        position: relative;
    }

    .swiper-nav {
        $size: 35px;
        position: absolute;
        width: $size;
        height: $size;
        top: calc(50% - #{$size * 0.5});
        border: none;
        background-color: transparent;
        padding: 0;
    }

    .swiper-next {
        left: 100%;
    }

    .swiper-prev {
        right: 100%;
    }

    .close-btn {
        width: 40px;
        height: 40px;
        background-color: #fff;
        border: none;
        border-radius: 50%;
        position: absolute;
        z-index: 2;
        top: -15px;
        right: -15px;
        padding: 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .swiper {
        height: 100%;
    }

    .arrow {
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 20px 15px 0 15px;
        border-color: #ffffff transparent transparent transparent;
    }

</style>
